import { el, btn, backButton } from '@/ui/dom';
import { confirmAction, closeConfirm } from '@/ui/confirm';
import type { Settings } from '@/core/SaveData';
import { DIFFICULTY_LEVELS, type DifficultyLevel } from '@/config/tuning';

/**
 * SettingsScreen.ts — difficulty, volumes, graphics quality, motion, touch
 * controls, HUD size, and the destructive actions (reset / sign out / delete).
 * Changes apply LIVE through onChange and persist immediately.
 *
 * LAYOUT (M115 owner round). The list is built ONCE in one order and rendered
 * two ways:
 *  · portrait + desktop — every wrapper is `display: contents`, so the whole
 *    thing collapses into the same single flex column it has always been.
 *  · landscape phones (the media block in ui.css) — DIFFICULTY spans the full
 *    width across the top, AUDIO + HUD flow down the left column and DISPLAY &
 *    CONTROLS down the right (`.settings-col`), then two full-width rows: the
 *    credits/about INFO row and, LAST, the DANGER ZONE (reset · sign out ·
 *    delete — owner 2026-09-16: "the very last thing at the bottom").
 *    Independent columns matter: the old version was one
 *    2-column grid, where each grid ROW is as tall as its tallest cell, so a
 *    tall block (DIFFICULTY, RACER ID) punched a big hole in the column beside
 *    it and pushed unrelated settings into each other. Two self-packing
 *    columns cannot produce that hole by construction.
 *  The two columns are simply a PREFIX and a SUFFIX of the one list, so the
 *  portrait order is unchanged when they collapse.
 */
export interface SettingsCallbacks {
  onChange: (settings: Settings) => void;
  onReset: () => void;
  onBack: () => void;
  /** M102b — sign out of the Racer ID (shown only when signed in, from the
   *  title). Settings is the ONE sign-out home (owner rule: signing out is
   *  the shared-computer exception, not a daily action). */
  onSignOut: () => void;
  /** M111 — permanently DELETE the Racer ID (store law: both app stores
   *  require an in-app deletion path). Resolves null on success / an error
   *  line to show. Lives beside SIGN OUT in the RACER ID group. */
  onDeleteAccount: (onStep: (text: string) => void) => Promise<string | null>;
  /** M111 — open the privacy policy (Apple requires an in-app link). */
  onPrivacy: () => void;
  /** M146 — open the Help & Support page (FAQ + contact form). */
  onSupport: () => void;
  /** M156 — open the Community Rules page (App Store 1.2: the terms every
   *  Racer ID agrees to — zero tolerance for offensive names and abuse). */
  onCommunityRules: () => void;
  /** M156 — the players this Racer ID blocked (unblock them). */
  onBlockedPlayers: () => void;
}

const QUALITY_OPTIONS = ['auto', 'low', 'medium', 'high'] as const;

/** The skill ladder wears its own colours (green → gold, the M55 ladder —
 *  same accents as the Quick Race chips so the setting reads as ONE thing). */
const DIFFICULTY_LABELS: Record<DifficultyLevel, { label: string; accent: string }> = {
  bigkid: { label: 'BIG KID', accent: '#49c0ff' }, // M83: the kids mode — sky blue, below the ladder
  easy: { label: 'EASY', accent: '#22c47e' },
  medium: { label: 'MEDIUM', accent: '#f0a028' },
  hard: { label: 'HARD', accent: '#ff6242' },
  legendary: { label: 'LEGEND', accent: '#e6b422' },
};

export class SettingsScreen {
  private root: HTMLDivElement;
  private settings!: Settings;
  private reducedMotion = false;
  private difficultyChips: HTMLButtonElement[] = [];
  private qualityChips: HTMLButtonElement[] = [];
  private motionBtn!: HTMLButtonElement;
  private leftyBtn!: HTMLButtonElement;
  private acctGroup!: HTMLDivElement;
  private deleteBtn!: HTMLButtonElement;
  private deleteStatus!: HTMLDivElement;
  private sliders: Partial<
    Record<'master' | 'sfx' | 'music' | 'hudScale' | 'touchSteerSensitivity', HTMLInputElement>
  > = {};

  constructor(
    parent: HTMLElement,
    private cb: SettingsCallbacks,
  ) {
    this.root = el('div', 'screen screen-settings screen-cine', parent);
    this.root.style.display = 'none';

    const panel = el('div', 'panel settings-panel', this.root);
    const head = el('div', 'mhead settings-head', panel);
    const kick = el('div', 'mhead-kicker', head);
    el('span', 'mhead-rule', kick);
    el('span', 'mhead-kicker-text', kick).textContent = 'TUNE YOUR GAME';
    el('span', 'mhead-rule', kick);
    const title = el('div', 'mhead-logotype settings-title', head);
    title.innerHTML = '<span class="mhead-chrome">SETTINGS</span>';

    const body = el('div', 'settings-body', panel);
    /** One label+control block. `display: contents` off-landscape (see above). */
    const group = (host: HTMLElement): HTMLDivElement => el('div', 'settings-group', host);

    // ---- DIFFICULTY: full width across the top (owner: "all on one line") ----
    // It leads the list (owner 2026-07-15: the most prominent spot) — the
    // setting for EVERY offline race, Quick Race and Tour alike.
    const dfGroup = el('div', 'settings-group settings-group-wide', body);
    const dfLabel = el('div', 'settings-label', dfGroup);
    dfLabel.textContent = 'DIFFICULTY (ALL OFFLINE RACES)';
    const dfRow = el('div', 'settings-chips settings-chips-wide', dfGroup);
    for (const level of DIFFICULTY_LEVELS) {
      const chip = btn(DIFFICULTY_LABELS[level].label, 'chip', dfRow, () => {
        this.settings.difficulty = level;
        this.refresh();
        this.cb.onChange(this.settings);
      });
      chip.style.setProperty('--chip-accent', DIFFICULTY_LABELS[level].accent);
      this.difficultyChips.push(chip);
    }

    // ================= COLUMN A: AUDIO + HUD =================
    const colA = el('div', 'settings-col', body);

    // AUDIO — the three volumes as ONE block (owner: "all the volumes are
    // properly grouped"). The heading only appears in the landscape layout,
    // where the block is a real cell; portrait keeps its flat list unchanged.
    const audio = el('div', 'settings-sub', colA);
    el('div', 'settings-sub-head', audio).textContent = 'AUDIO';
    this.sliderRow(group(audio), 'MASTER VOLUME', 'master', 0, 100);
    this.sliderRow(group(audio), 'SOUND EFFECTS', 'sfx', 0, 100);
    this.sliderRow(group(audio), 'MUSIC', 'music', 0, 100);

    this.sliderRow(group(colA), 'HUD SIZE', 'hudScale', 85, 130);

    // ================= COLUMN B: DISPLAY + CONTROLS =================
    // Owner 2026-09-16 (landscape phones): graphics / motion / steering sit
    // BESIDE the audio block on the right, in their own boxed group, so the
    // two columns read as two families of settings and land at similar
    // heights (the old split left this column short and the other long).
    const colB = el('div', 'settings-col', body);
    const display = el('div', 'settings-sub', colB);
    el('div', 'settings-sub-head', display).textContent = 'DISPLAY & CONTROLS';

    const qGroup = group(display);
    const qLabel = el('div', 'settings-label', qGroup);
    qLabel.textContent = 'GRAPHICS QUALITY';
    const qRow = el('div', 'settings-chips', qGroup);
    for (const q of QUALITY_OPTIONS) {
      const chip = btn(q.toUpperCase(), 'chip', qRow, () => {
        this.settings.quality = q;
        this.refresh();
        this.cb.onChange(this.settings);
      });
      this.qualityChips.push(chip);
    }

    // (M87: the CPU RIVALS row is gone — every offline race is you + 6 CPUs.)

    const mGroup = group(display);
    const mLabel = el('div', 'settings-label', mGroup);
    mLabel.textContent = 'MOTION';
    this.motionBtn = btn('', 'btn btn-secondary settings-toggle', mGroup, () => {
      this.settings.reducedMotion = !this.settings.reducedMotion;
      this.reducedMotion = this.settings.reducedMotion;
      this.refresh();
      this.cb.onChange(this.settings);
    });

    const cGroup = group(display);
    const cLabel = el('div', 'settings-label', cGroup);
    cLabel.textContent = 'TOUCH CONTROLS';
    this.leftyBtn = btn('', 'btn btn-secondary settings-toggle', cGroup, () => {
      this.settings.lefty = !this.settings.lefty;
      this.refresh();
      this.cb.onChange(this.settings);
    });

    this.sliderRow(group(display), 'STEERING SENSITIVITY', 'touchSteerSensitivity', 50, 150);

    // M115 (owner): the STEERING SCHEME chips + the tilt enable/re-centre
    // button are GONE — tilt steering never worked well enough to ship as an
    // option. The gyro code is parked in src/input/Tilt.ts for a future round;
    // nothing enables it, so the game never asks for motion permission.

    // ================= INFO ROW: credits + about =================
    // Full width under the two columns (two cells side by side in landscape).
    const info = el('div', 'settings-group-wide settings-info', body);

    // M82: attribution for the licensed city photography (CC BY-SA / FAL —
    // full per-photo credits ship in public/cities/CREDITS.md).
    const creditsGroup = group(info);
    el('div', 'settings-label', creditsGroup).textContent = 'CITY PHOTOGRAPHY';
    const credits = el('div', 'settings-credits', creditsGroup);
    credits.textContent =
      'Photos by Dllu, Fuzzypiggy, Wolfgang Moroder, Akonnchiroll, Arne Müseler, ' +
      'Tafsir207, S.aderogba, A.Savin and Almonroth via Wikimedia Commons ' +
      '(CC BY-SA / FAL, cropped).';

    // M111 — the privacy policy, in-app (Apple 5.1.1(i) requires an easily
    // accessible link inside the app, not just on the store listing).
    const aboutGroup = group(info);
    el('div', 'settings-label', aboutGroup).textContent = 'ABOUT';
    // M146 (owner): HELP & SUPPORT first, the FAQ + contact form page.
    btn('HELP & SUPPORT', 'btn btn-secondary settings-toggle', aboutGroup, () => {
      this.cb.onSupport();
    });
    btn('PRIVACY POLICY', 'btn btn-secondary settings-toggle', aboutGroup, () => {
      this.cb.onPrivacy();
    });
    btn('COMMUNITY RULES', 'btn btn-secondary settings-toggle', aboutGroup, () => {
      this.cb.onCommunityRules();
    });

    // ================= DANGER ZONE: the very last thing =================
    // Owner 2026-09-16: reset progress, sign out and delete Racer ID sit at
    // the BOTTOM of the page in every layout (they used to hang beside the
    // audio block in landscape). A rule line above them marks the section.
    const danger = el('div', 'settings-group-wide settings-danger', body);

    const dGroup = group(danger);
    const dangerLabel = el('div', 'settings-label settings-label-danger', dGroup);
    dangerLabel.textContent = 'PROGRESS';
    btn('RESET ALL PROGRESS', 'btn btn-danger-ghost', dGroup, () => {
      confirmAction({
        title: 'RESET ALL PROGRESS?',
        body:
          'This wipes your tour, every city record and best time on this device, ' +
          'and puts you back at the start. It cannot be undone.',
        confirmLabel: 'RESET EVERYTHING',
        reducedMotion: this.reducedMotion,
        onConfirm: () => this.cb.onReset(),
      });
    });

    // M102b — RACER ID: the one sign-out home. The whole group hides unless
    // the player is signed in (and settings was opened from the title).
    this.acctGroup = group(danger);
    el('div', 'settings-label settings-label-danger', this.acctGroup).textContent = 'RACER ID';
    btn('SIGN OUT ON THIS DEVICE', 'btn btn-danger-ghost', this.acctGroup, () => {
      confirmAction({
        title: 'SIGN OUT?',
        body:
          'This device goes back to guest mode. Your career, XP, SP and garage ' +
          'stay safe on your Racer ID — sign back in any time to pick them up.',
        confirmLabel: 'SIGN OUT',
        reducedMotion: this.reducedMotion,
        onConfirm: () => this.cb.onSignOut(),
      });
    });
    const note = el('div', 'settings-credits', this.acctGroup);
    note.textContent =
      'Your career stays safe on your Racer ID — this device just goes back to guest mode.';
    // M156 — the players you blocked (from a race lobby, the results or the
    // Racer Club) — unblock them here.
    btn('BLOCKED PLAYERS', 'btn btn-secondary settings-toggle', this.acctGroup, () => {
      this.cb.onBlockedPlayers();
    });
    // M111 — DELETE MY RACER ID (both app stores require an in-app deletion
    // path). M115: the old inline two-tap became the shared confirm dialog,
    // whose copy spells out that deletion is total and permanent.
    this.deleteBtn = btn('DELETE MY RACER ID', 'btn btn-danger-ghost', this.acctGroup, () => {
      confirmAction({
        title: 'DELETE YOUR RACER ID?',
        body:
          'This permanently erases your account: email, username, every Skill Point, ' +
          'purchase and record. It is gone forever and cannot be recovered.',
        confirmLabel: 'DELETE FOREVER',
        reducedMotion: this.reducedMotion,
        onConfirm: () => this.runDelete(),
      });
    });
    this.deleteStatus = el('div', 'settings-credits settings-delete-status', this.acctGroup);
    const delNote = el('div', 'settings-credits', this.acctGroup);
    delNote.textContent =
      'Deleting removes your email, username, XP, SP and records from our servers — permanently.';

    // M114b (owner): the footer BACK button is gone — the ONE shared chevron
    // pins to the screen's upper-left, like every other menu.
    backButton(this.root, () => this.cb.onBack());
  }

  /** The confirmed deletion — unchanged behaviour, just moved behind the dialog. */
  private runDelete(): void {
    this.deleteBtn.disabled = true;
    this.deleteStatus.textContent = 'DELETING…';
    // M156: an Apple Racer ID confirms with Apple mid-way — the status line
    // follows the steps.
    void this.cb.onDeleteAccount((t) => (this.deleteStatus.textContent = t)).then((err) => {
      this.deleteBtn.disabled = false;
      // Success navigates away (Flow closes settings); only errors show.
      this.deleteStatus.textContent = err ?? '';
    });
  }

  private sliderRow(
    parent: HTMLElement,
    label: string,
    key: 'master' | 'sfx' | 'music' | 'hudScale' | 'touchSteerSensitivity',
    min: number,
    max: number,
  ): void {
    const lab = el('div', 'settings-label', parent);
    lab.textContent = label;
    const input = el('input', 'settings-slider', parent);
    input.type = 'range';
    input.min = String(min);
    input.max = String(max);
    input.setAttribute('aria-label', label);
    input.addEventListener('input', () => {
      const v = Number(input.value) / 100;
      if (key === 'hudScale') this.settings.hudScale = v;
      else this.settings[key] = v;
      this.paintFill(input);
      this.cb.onChange(this.settings);
    });
    this.sliders[key] = input;
  }

  /**
   * M115: the sliders are now fully custom-styled so the THUMB can be a real
   * finger target (`appearance: none` in ui.css). That switches off the
   * browser's `accent-color` fill, so the gold "how far along" bar is painted
   * from here: `--fill` is the percentage the track gradient stops at.
   */
  private paintFill(input: HTMLInputElement): void {
    const min = Number(input.min);
    const max = Number(input.max);
    const pct = max > min ? ((Number(input.value) - min) / (max - min)) * 100 : 0;
    input.style.setProperty('--fill', `${pct}%`);
  }

  private refresh(): void {
    this.sliders.master!.value = String(Math.round(this.settings.master * 100));
    this.sliders.sfx!.value = String(Math.round(this.settings.sfx * 100));
    this.sliders.music!.value = String(Math.round(this.settings.music * 100));
    this.sliders.hudScale!.value = String(Math.round(this.settings.hudScale * 100));
    this.sliders.touchSteerSensitivity!.value = String(
      Math.round(this.settings.touchSteerSensitivity * 100),
    );
    for (const s of Object.values(this.sliders)) if (s) this.paintFill(s);
    this.difficultyChips.forEach((chip, i) =>
      chip.classList.toggle('on', DIFFICULTY_LEVELS[i] === this.settings.difficulty),
    );
    this.qualityChips.forEach((chip, i) =>
      chip.classList.toggle('on', QUALITY_OPTIONS[i] === this.settings.quality),
    );
    this.motionBtn.textContent = this.settings.reducedMotion
      ? 'REDUCED MOTION: ON'
      : 'REDUCED MOTION: OFF';
    this.leftyBtn.textContent = this.settings.lefty
      ? 'LEFTY — JOYSTICK RIGHT'
      : 'RIGHTY — JOYSTICK LEFT';
  }

  show(settings: Settings, signedIn = false): void {
    this.settings = settings;
    this.reducedMotion = settings.reducedMotion;
    closeConfirm();
    this.deleteStatus.textContent = '';
    this.acctGroup.style.display = signedIn ? '' : 'none';
    this.refresh();
    this.root.style.display = '';
  }

  hide(): void {
    // A dialog is modal to THIS screen — leaving must never strand it on top
    // of whatever comes next (including an edge swipe-back, M114b).
    closeConfirm();
    this.root.style.display = 'none';
  }

  dispose(): void {
    closeConfirm();
    this.root.remove();
  }
}
